import { getAnalytics } from 'firebase/analytics';
import { initializeApp } from 'firebase/app';
import {
  GoogleAuthProvider,
  NextFn,
  OAuthProvider,
  User,
  connectAuthEmulator,
  createUserWithEmailAndPassword,
  getAuth,
  signInAnonymously,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
} from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getFunctions } from 'firebase/functions';
import { connectStorageEmulator, getStorage } from 'firebase/storage';
import { baseUrl } from '../utils/environments';

class FirebaseConnector {
  /**
   * The Firebase configuration exported from the console.
   */
  private readonly firebaseConfig;
  private readonly googleProvider;
  private readonly microsoftProvider;
  private readonly functions;
  private readonly app;
  public readonly analytics;
  private readonly auth;
  private readonly firestore;
  public readonly storage;
  public readonly contentStorage;

  constructor() {
    this.firebaseConfig = {
      apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
      authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
      projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
      storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
      appId: import.meta.env.VITE_FIREBASE_APP_ID,
      measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
    };
    this.googleProvider = new GoogleAuthProvider();
    this.microsoftProvider = new OAuthProvider('microsoft.com');
    this.app = initializeApp(this.firebaseConfig);
    this.auth = getAuth(this.app);
    this.firestore = getFirestore(this.app);
    this.functions = getFunctions(this.app);
    this.storage = getStorage(this.app);
    this.contentStorage = getStorage(this.app, import.meta.env.VITE_FIREBASE_CONTENT_STORAGE_BUCKET);

    if (
      import.meta.env.DEV ||
      window.location.hostname.toLowerCase() === 'localhost'
    ) {
      console.log('⮑ Starting application in development mode.');
      connectAuthEmulator(this.auth, 'http://localhost:9099', {
        disableWarnings: true,
      });
      //connectFirestoreEmulator(this.db, "localhost", 8080);
      //connectFunctionsEmulator(this.functions, "localhost", 5001);
      connectStorageEmulator(this.storage, 'localhost', 9199);
      connectStorageEmulator(this.contentStorage, 'localhost', 9199);
    } else {
      this.analytics = getAnalytics(this.app);
    }

    this.auth.onAuthStateChanged(
      this.handleUserStateChanged as NextFn<User | null>,
    );

    this.auth.onIdTokenChanged(
      this.handleTokenStateChanged as NextFn<User | null>,
    );

    console.log('Connector initialized');
  }

  /**
   * The Firestore instance we're connected to.
   */
  public get db() {
    return this.firestore;
  }

  /**
   * The Firebase authentication connector.
   */
  public get authentication() {
    return this.auth;
  }

  /**
   * The Firebase functions connector.
   */
  public get fn() {
    return this.functions;
  }

  /**
   * Gets the authorization token for the current user.
   */
  public async getCurrentUserToken(refresh?: boolean) {
    return this.auth.currentUser?.getIdToken(!!refresh);
  }

  /**
   * Invoked when the token state changes; this can occur when the token is
   * refreshed automatically.  Triggers 3 times on sign in, sign out, and refresh.
   * @param user The user
   */
  private async handleTokenStateChanged(user: User) {
    if (!user) {
      console.log(` 🔐 Token state changed; no user present`);

      $profileLoader.set(false);

      return;
    }

    if ($profile.value) {
      console.log(
        ` 🔐 Profile and claim already present: ${$profile.value?.email}`,
      );
      return;
    }

    console.log(` 🔐 Setting user`);

    window.setTimeout(() => {
      // There's an error on the API call to get profile if this happens too fast?
      setUser(user).then(async () => {
        console.log(` 🔐 Current location: ${window.location.href}`);

        if (window.location.href === baseUrl) {
          return;
        }

        const params = new URLSearchParams(window.location.search);
        let redirect = params.get('redirect') ?? 'home';

        if (redirect === '/') {
          redirect = 'home';
        }

        console.log(` 🔐 Redirect: ${redirect}`);

        const target = `${baseUrl}${redirect}`;

        // Only redirect if we're not already on the page and it's not the landing page
        if (target !== window.location.href) {
          console.log(` 🔐 Redirecting user to ${redirect}`);

          // @ts-ignore
          await $router.open(redirect);
        }
      }),
        50;
    });
  }

  /**
   * Invoked when the user state is changed and the user is logged in.  This is
   * not as reliable since the user may already be considered logged in to the
   * SSO account so it may not trigger in all cases such as when the token is
   * still valid.
   * @param user The user
   */
  private handleUserStateChanged(user: User) {
    console.log(` 🔐 User state: ${user?.email ?? 'logged out'}`);

    if (!user || !window.location.href.includes('login')) {
      console.log(` 🔐 No user or the URL is not the login page.`);
      return;
    }
  }

  /**
   * Creates a new user using the username and password
   * @param username The username supplied
   * @param password The password supplied
   */
  public async createEmailUser(username: string, password: string) {
    await createUserWithEmailAndPassword(this.auth, username, password);
  }

  /**
   * Performs a sign in with the supplied login name and password
   * @param username The username supplied
   * @param password The password supplied
   */
  public async loginEmailUser(username: string, password: string) {
    await signInWithEmailAndPassword(this.auth, username, password);
  }

  /**
   * Performs a login and then redirects the user to the requested page.
   */
  public async loginGoogle() {
    await this.login(this.googleProvider);
  }

  /**
   * Performs a login and then redirects the user to the requested page.
   */
  public async loginMicrosoft() {
    await this.login(this.microsoftProvider);
  }

  /**
   * Performs the authentication using the designated method and then waits for the
   * serverless function to create a profile for the user.  We wait and check for a
   * profile periodically as it can take up to 6 seconds for the function to come
   * on line from a cold start.
   * @param provider The provider to use for performing the authentication. When this value is null, use anonymous.
   */
  private async login(
    provider: GoogleAuthProvider | OAuthProvider | undefined,
  ) {
    const authMethod = provider
      ? () => signInWithPopup(this.auth, provider)
      : () => signInAnonymously(this.auth);

    try {
      const result = await authMethod();

      // this.handleUserStateChanged(result.user);
    } catch (e) {
      console.error('An error occurred during login.');
      console.error(e);
    }
  }

  /**
   * Performs a logout and clears the current user from the store.
   */
  public async logout() {
    console.log(' 🔐 Logout');

    await signOut(this.auth);

    try {
      clearUser();

      // This event triggers the subscriptions to be disposed from FirebaseSubscriptions.ts
      document.dispatchEvent(new Event('__logout'));

      window.location.href = '/login';
    } catch (error) {
      console.log(error);
    }
  }
}

export const firebaseConnector = new FirebaseConnector();
